/**
 * Math subject constants for Singapore secondary curriculum.
 * A-Math (Additional Mathematics) and E-Math (Elementary Mathematics) apply
 * to G2/G3 students in Sec 3/4. G1, IP, and IB stay single-subject streams
 * without A-Math/E-Math selection.
 */

import type { ExamLevel } from './examLevels.js';
import type { OrgLevel } from './orgLevels.js';

export const MATH_SUBJECTS = Object.freeze(['E-Math', 'A-Math'] as const);

export type MathSubject = (typeof MATH_SUBJECTS)[number];

const APPLICABLE_EXAM_LEVELS: readonly string[] = ['G2', 'G3'];
const APPLICABLE_ORG_LEVELS: readonly string[] = ['secondary_3', 'secondary_4'];

/** Whether math subject selection (A-Math/E-Math) applies for this student's level. */
export function isMathSubjectApplicable(
  examLevel: ExamLevel | string | null | undefined,
  orgLevel: OrgLevel | string | null | undefined
): boolean {
  if (!examLevel || !orgLevel) return false;
  return (
    APPLICABLE_EXAM_LEVELS.includes(examLevel) &&
    APPLICABLE_ORG_LEVELS.includes(orgLevel)
  );
}

/**
 * Untagged is universal: an entity with no `mathSubjects` tag belongs to the
 * common foundation and is visible under either subject focus. The split is
 * real only in Sec 3/4 at G2/G3 (see `isMathSubjectApplicable`), so everything
 * below that line — the whole Sec 1–2 foundation, every G1/IP/IB topic — is
 * correctly untagged and must survive a focused view. A Sec 4 G3 student who
 * focuses on E-Math still needs their Sec 2 algebra remediation.
 *
 * Strict array membership would drop those rows, which is why this predicate
 * exists in one place rather than being respelled per call site. It is the
 * in-memory twin of `buildMathSubjectNodeFilter`
 * (`server/services/mathSubjectNodeFilter.ts`) and of the question-layer
 * `effectiveMathSubjects` clause in `buildQuestionLevelFilter`.
 *
 * No focus requested (`mathSubject` falsy) means no filtering at all.
 */
export function matchesMathSubject(
  tags: readonly string[] | null | undefined,
  mathSubject: MathSubject | string | null | undefined
): boolean {
  if (!mathSubject) return true;
  if (tags === null || tags === undefined || tags.length === 0) return true;
  return tags.includes(mathSubject);
}
